import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";
import type { ContactBatchExportFormat } from "./contact-batch-export";

const FORMATS: ContactBatchExportFormat[] = ["CSV", "XLSX", "PDF"];

export function ContactBatchExportModal({
  batchName,
  rowCount,
  truncatedNote,
  onExport,
  onClose,
}: {
  batchName: string;
  rowCount: number;
  truncatedNote?: string | null;
  onExport: (format: ContactBatchExportFormat) => Promise<void>;
  onClose: () => void;
}) {
  const toast = useToast();
  const [format, setFormat] = useState<ContactBatchExportFormat>("CSV");
  const [exporting, setExporting] = useState(false);
  const subtitle = truncatedNote
    ? truncatedNote
    : `${batchName} · ${rowCount} row${rowCount === 1 ? "" : "s"}`;

  const startExport = async () => {
    try {
      setExporting(true);
      await onExport(format);
      onClose();
    } catch (error) {
      toast.danger("Export failed", (error as Error).message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      title="Export Contacts"
      subtitle={subtitle}
      onClose={onClose}
      width="max-w-lg"
      footer={(
        <>
          <button
            disabled={exporting}
            onClick={onClose}
            className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            disabled={exporting}
            onClick={startExport}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-wait disabled:opacity-60"
          >
            {exporting ? "Preparing..." : "Download Export"}
          </button>
        </>
      )}
    >
      <div>
        <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Format</p>
        <div className="inline-flex rounded-lg border border-line p-1">
          {FORMATS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setFormat(item)}
              className={cn(
                "rounded-md px-4 py-1.5 text-sm font-semibold transition-colors",
                format === item ? "bg-brand text-white" : "text-ink-2 hover:bg-soft",
              )}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
